import { ApiError, ApiException } from '@/utils/errorHandler';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://www.getpoints.com.br';

async function apiFetch<T>(method: string, endpoint: string, body?: unknown): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  try {
    const res = await fetch(`${API_BASE_URL}${endpoint}`, {
      method,
      headers,
      body: body ? JSON.stringify(body) : undefined,
    });

    const json = await res.json().catch(() => null);

    if (!res.ok) {
      const errorPayload: ApiError = {
        timestamp: json?.timestamp || new Date().toISOString(),
        status: json?.status || res.status,
        error: json?.error || 'Unexpected Error',
        message: json?.message || 'Erro inesperado',
        path: json?.path || endpoint,
        traceId: json?.traceId || 'unknown',
        details: json?.details || [],
      };
      throw new ApiException(errorPayload);
    }

    return json as T;
  } catch (err) {
    // Re-throw ApiException as-is
    if (err instanceof ApiException) {
      throw err;
    }

    // Convert network errors to ApiException
    if (err instanceof Error) {
      const errorPayload: ApiError = {
        timestamp: new Date().toISOString(),
        status: 0,
        error: 'Network Error',
        message: err.message === 'Failed to fetch' || err.message === 'NetworkError when attempting to fetch resource.'
          ? 'Servidor indisponível no momento. Verifique sua conexão e tente novamente.'
          : err.message,
        path: endpoint,
        traceId: 'unknown',
        details: ['Erro de conexão. Verifique se o servidor está online ou se há problemas de rede.'],
      };
      throw new ApiException(errorPayload);
    }

    throw err;
  }
}

// ─── Password Reset Request ───
export interface PasswordResetRequestPayload {
  login: string;
  canal: 'email' | 'sms';
  deviceFingerprint: string;
}

export interface PasswordResetRequestData {
  emailMascarado?: string;
  celularMascarado?: string;
}

export interface PasswordResetRequestResponse {
  data: PasswordResetRequestData;
}

export async function requestPasswordReset(payload: PasswordResetRequestPayload): Promise<PasswordResetRequestResponse> {
  return apiFetch<PasswordResetRequestResponse>('POST', '/api/v1/auth/password-reset/request', payload);
}

// ─── Password Reset Validate ───
export interface PasswordResetValidatePayload {
  login: string;
  code: string;
  deviceFingerprint: string;
}

export interface PasswordResetValidateData {
  resetToken: string;
}

export interface PasswordResetValidateResponse {
  data: PasswordResetValidateData;
}

export async function validatePasswordReset(payload: PasswordResetValidatePayload): Promise<PasswordResetValidateResponse> {
  return apiFetch<PasswordResetValidateResponse>('POST', '/api/v1/auth/password-reset/validate', payload);
}

// ─── Password Reset Confirm ───
export interface PasswordResetConfirmPayload {
  login: string;
  resetToken: string;
  novaSenha: string;
  confirmarSenha: string;
  deviceFingerprint: string;
}

export async function confirmPasswordReset(payload: PasswordResetConfirmPayload): Promise<void> {
  return apiFetch<void>('POST', '/api/v1/auth/password-reset/confirm', payload);
}